import constant from "../config/constant";

// antd's <Upload> with `beforeUpload` returning false does NOT upload the file
// itself; it stores an UploadFile *wrapper* in the form value. The wrapper has
// a `name` string but is not a real File, so appending it to FormData
// serializes to the literal string "[object Object]" and the backend never
// receives any bytes. The native File lives on `originFileObj`.
//
// This helper centralizes pulling the real File out of every shape antd may
// hand us so the logic can be unit tested in isolation.
export function extractFirmwareFile(values) {
  const candidate =
    values?.firmware?.fileList?.[0]?.originFileObj ??
    values?.firmware?.file?.originFileObj ??
    values?.firmware?.file ??
    null;

  return candidate;
}

// File extends Blob in the browser and in jsdom, so an `instanceof Blob` check
// accepts real uploads while rejecting the antd UploadFile wrapper (which is a
// plain object that merely has a `name` property).
export function isValidFirmwareFile(file) {
  return Boolean(file) && typeof Blob !== "undefined" && file instanceof Blob;
}

// Builds the multipart payload the backend expects. Returns either
// `{ formData, file, version }` on success or `{ error }` with a
// user-presentable message so the caller can surface it.
export function buildFirmwareFormData(values) {
  const file = extractFirmwareFile(values);

  if (!isValidFirmwareFile(file)) {
    return { error: "Please select a valid firmware file." };
  }

  const version = values?.firmware_version;
  if (!version || typeof version !== "string" || version.trim() === "") {
    return { error: "Please provide a firmware version." };
  }

  const formData = new FormData();
  formData.append("firmware", file);
  formData.append("version", version);

  return { formData, file, version };
}

// Normalizes the many ways apiRequest can report success/failure into a single
// boolean, so the UI reliably knows whether the upload actually worked.
export function isFirmwareUploadSuccess(res) {
  return res?.statusCode === constant.RESPONSE_CODE.SUCCESS;
}

// Extracts the most useful error message from a failed backend response.
export function getFirmwareUploadError(res) {
  return (
    res?.message ||
    res?.error ||
    res?.data?.message ||
    "Failed to upload firmware"
  );
}

export const V2_CDN_OFFER = {
  version: "1.5.2",
  hardware_version: "pcb-v3",
  url: "https://cdn1.myfigo.cloud/firmware/prod/pcb-v3/firmware-1.5.2.bin",
};

function asOffer(raw) {
  if (!raw || typeof raw !== "object" || !raw.version) return null;
  return {
    version: raw.version,
    url: raw.url,
    hardware_version: raw.hardware_version,
  };
}

// GET /admin/settings/firmware returns the v1 fleet pointer at the top
// level and the V2-app offer on `v2`. They must stay separate so Home /
// Firmware never imply 1.5.2 replaced 1.4.1.
export function parseFirmwareOffers(res) {
  if (!isFirmwareUploadSuccess(res)) return null;
  const data = res.data || {};
  return {
    v1: asOffer(data),
    v2: asOffer(data.v2),
  };
}

// Maps a GET /firmware response into the table rows the page renders. Returns
// null on any non-success/undefined response (apiRequest resolves to undefined
// on network errors) so callers never dereference a missing statusCode.
export function parseLatestFirmware(res) {
  const offers = parseFirmwareOffers(res);
  if (!offers) return null;
  if (!offers.v1) return [];

  return [
    {
      id: 1,
      firmware_version: offers.v1.version,
      firmware_url: offers.v1.url,
    },
  ];
}
